<script setup>
import { ref, computed, watch } from "vue"
import { storage } from '../storage'
import { galaxies, shipLevels } from '../staticData'
import { backgroundMusic, movePlanet } from '../helpers/renderer.js'
import { stringNumber } from '../helpers/functions.js'

import config from '../set_config.json'

import CoinsBoostBlock from './CoinsBoostBlock'
import GalaxiesList from './GalaxiesList'
import ShipsShop from './ShipsShop'
import UpgradeGalaxy from './Upgrades/UpgradeGalaxy'
import UpgradesShop from "./Upgrades/UpgradesShop"
import AchievementsList from "./Achievements/AchievementsList"

const props = defineProps(['earningsPerS', 'shipsNum', 'topClasses', 'activateBoost'])
const emits = defineEmits(['set-new-audio', 'show-notification'])

function resetGame() {
    // localStorage.removeItem("space_bar_clicker");
    for (const key in storage.ALIENS_OWNED) {
        storage.ALIENS_OWNED[key].amount = 0
    }
    for (const key in storage.UPGRADES_LVLS) {
        if (typeof storage.UPGRADES_LVLS[key] === 'number') storage.UPGRADES_LVLS[key] = 0
        else {
            for (const ind in storage.UPGRADES_LVLS[key]) {
                storage.UPGRADES_LVLS[key][ind] = 0
            }
        }
    }
    storage.GALAXY_IND = 0;
    storage.COINS = 0n;
    storage.PINK_COINS = 0;
    storage.BLACK_COINS = 0;
    storage.VERSION = config.version
    storage.CURRENT_SHIP = 'stick'
    storage.RESET = false
}

const showGalaxiesList = ref(false)
function toggleGalaxies() {
    showGalaxiesList.value = !showGalaxiesList.value
}
const showUpgrades = ref(false)
function toggleUpgradeMessage() {
    showUpgrades.value = !showUpgrades.value
}
const userMsg = ref(null)


const nextPlanetUrl = computed(() => galaxies[storage.GALAXY_IND + 1] && `/spacebar-clicker/assets/planets/planet_${storage.GALAXY_IND + 1}.png`)
const isEnoughForJump = computed(() => {
    const nextG = galaxies[storage.GALAXY_IND + 1]
    if (nextG && nextG.distance) {
        const coins = Number(storage.COINS)
        if (nextG.distance <= coins) {
            movePlanet(1)
        } else if (coins < nextG.distance && coins >= nextG.distance * 0.8) {
            movePlanet(0.8)
        } else if (coins < nextG.distance * 0.8 && coins >= nextG.distance * 0.6) {
            movePlanet(0.6)
        } else if (coins < nextG.distance * 0.6 && coins >= nextG.distance * 0.4) {
            movePlanet(0.4)
        } else if (coins < nextG.distance * 0.4 && coins >= nextG.distance * 0.2) {
            movePlanet(0.2)
        }
    }
    return storage.COINS >= nextG.distance
})

const showUpgradesShop = ref(false)

function toggleMusic() {
    storage.MUSIC_ON = !storage.MUSIC_ON
    if (storage.MUSIC_ON) backgroundMusic.play()
    else backgroundMusic.pause()
}

const achievementsDisplay = ref('none')
const showNewAchiev = ref(false)
function hideAchievements() {
    achievementsDisplay.value = 'none'
    showNewAchiev.value = false
}
function newAchievement() {
    emits('show-notification', 2)
    showNewAchiev.value = true
    // setTimeout(() => {
    //     showNewAchiev.value = false
    // }, 2000);
}

const showShipShop = ref(false)
const showNewShip = ref(false)

watch(storage.SHIP_OWNED, () => {
    // emits('show-notification', 1)
    showNewShip.value = true
})
function openShipShop() {
    showShipShop.value = true
    showNewShip.value = false
}
const clicksInPers = computed(() => {
    const res = storage.CLICKS_ALL * 100 / shipLevels[props.shipsNum]
    return res + '%'
})
</script>

<template>
    <!-- <div class="main-wrap"> -->
    <div>
        <div class="top-header">
            <h3 class="header-name" @click="toggleGalaxies">{{ galaxies[storage.GALAXY_IND] && galaxies[storage.GALAXY_IND].name }}</h3>
            <CoinsBoostBlock :earningsPerS="props.earningsPerS" :activateBoost="activateBoost"/>
        </div>

        <div class="top-left" :class="[topClasses]">
            <div class="menu">
                <div class="music-btn-wrap">Sounds  
                    <div class="music-btn btn-play" v-show="!storage.MUSIC_ON" @click="toggleMusic">
                        <span />
                    </div>
                    <div class="music-btn btn-stop" v-show="storage.MUSIC_ON" @click="toggleMusic">
                        <span /><span />
                    </div>
                </div>
                <button class="action-btn" :class="{ 'aura-on': isEnoughForJump }" @click="toggleUpgradeMessage" v-if="galaxies[storage.GALAXY_IND + 1]">
                    Next galaxy <span class="btn-span" style="color: red;font-weight: bold;">↝ </span>
                    <img :src="nextPlanetUrl" alt="" class="menu-btn-img">
                </button>
                <button class="action-btn" @click="showUpgradesShop = true" >
                    <img class="menu-btn-img-mini" src="/spacebar-clicker/assets/balls/ball_1.png" alt="">
                    Upgrades Shop
                    <img class="menu-btn-img-mini" src="/spacebar-clicker/assets/balls/ball_9.png" alt="">
                </button>
                <button class="action-btn" @click="achievementsDisplay = 'block'" :class="{ 'notification-on': showNewAchiev }">
                    Achievements
                    <img class="menu-btn-img" src="/spacebar-clicker/assets/awards/award_6.png" alt="">
                    <span class="notif-dot" />
                </button>
                <button class="action-btn" @click="openShipShop" :class="{ 'notification-on': showNewShip }" >
                    Ships Hangar
                    <img class="menu-btn-img" src="/spacebar-clicker/assets/pics/box.png" alt="">
                    <span class="notif-dot" />
                </button>
                <button class="action-btn reset-btn" v-if="storage.RESET || config.isTest" @click="resetGame">Reset game 🔄</button>
            </div>
            <div class="clicks-block to-center" >
                <template v-if="shipLevels[shipsNum]">
                    <p>Unlock Next ship</p>
                    <div class="range" :class="{ 'glow-on': storage.GLOW_ON }">
                        <div class="full" :style="{ 'width': clicksInPers }" />
                        <span>{{ stringNumber(storage.CLICKS_ALL, true) }} /</span>
                        <span>{{ stringNumber(shipLevels[shipsNum], true) }}</span>
                        <!-- <p> </p> -->
                    </div>
                </template>
            </div>
        </div>
    </div>
    <GalaxiesList v-if="showGalaxiesList"  @hide-list="toggleGalaxies" />
    <UpgradeGalaxy v-if="showUpgrades" :galaxy="galaxies[storage.GALAXY_IND + 1]" @hide-window="toggleUpgradeMessage" @set-new-audio="$emit('set-new-audio')" />
    <UpgradesShop v-if="showUpgradesShop" @hide-shop="showUpgradesShop = false"/>
    <!-- v-if="showAchievements"  -->
    <AchievementsList 
    :display="achievementsDisplay"
    @hide-achievements="hideAchievements" 
    @show-new-achievement="newAchievement"/>

    <ShipsShop v-if="showShipShop" @hide-shop="showShipShop = false"  />
    <p class="user-msg" v-if="userMsg" >{{ userMsg }}</p>
</template>

<style scoped>
.action-btn {
    transition: box-shadow 1s;
    position: relative;
}

.action-btn .menu-btn-img {
    height: 30px;
    width: auto;
    max-width: none;
}

.action-btn .menu-btn-img-mini {
    height: 25px;
    width: auto;
    max-width: none;
}

.notif-dot {
    position: absolute;
    top: 0;
    right: 0;
    display: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: red;
    box-shadow: 0 0 5px 5px red;
}

.notification-on.action-btn {
    overflow: visible;
}

.notification-on .notif-dot {
    display: block;
}

.main-wrap {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
}

.music-btn-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px;
    margin-top: 5px;
    overflow: visible;
}

.music-btn {
    /* min-height: 34px; */
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #ffe100;
    position: relative;
    padding: 6px;
    margin-left: 10px;
    box-shadow: 0px 0px 2px 4px #ffffffeb;
}

.music-btn:hover {
    cursor: pointer;
}

.btn-stop {
    display: flex;
    justify-content: space-between;
}

.btn-stop span {
    /* display: block; */
    height: 100%;
    width: 7px;
    border-radius: 5px;
    background-color: white;
}

.btn-play span {
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-right: 12px solid transparent;
    border-bottom: 18px solid white;
    display: inline-block;
    /* position: absolute; */
    /* top: 50%; */
    /* left: 50%; */
    /* transform: translate(-50%, -50%); */

    /* left: 10px; */
    /* top: 6px; */
    transform: rotate(90deg);
}

/* .music-btn {
    height: 40px;
    width: auto;
} */

.header {
    position: absolute;
    top: 50px;
    right: 0;
    width: 100%;
    /* height: 300px; */
    display: flex;
    justify-content: flex-end;
}



.row-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
}

.top-header {
    position: absolute;
    top: 10px;
    /* left: 45%; */
    text-align: center;
    display: flex;
    flex-direction: column;
    /* position: absolute; */
    /* top: 50%; */
    left: 50%;
    transform: translate(-50%);
    overflow: visible;
}

.top-left {
    position: absolute;
    top: 20px;
    left: 20px;
    overflow: visible;
    height: 100vh;
    /* display: flex;
    flex-direction: column;
    border-radius: 0.625rem;
    align-items: center;
    justify-content: center;
    width: max-content;
    padding: 10px;
    box-shadow: inset 0px 0px 35px 9px #00beff; */
}



.menu {
    display: flex;
    flex-direction: column;
    border-radius: 0.625rem;
    align-items: center;
    justify-content: center;
    width: max-content;
    padding: 10px;
    box-shadow: inset 0px 0px 35px 9px #00beff;
    max-height: 80%;
}

.clicks-block {
    margin-top: 5px;
    width: 100%;
    overflow: visible;
}

.clicks-block p {
    text-align: center;
}

.clicks-block .range {
    position: relative;
    width: 99%;
    margin: 5px 0;
    /* height: 1.2rem; */
    border-radius: 20px;
    border: 2px solid yellow;
    transition: box-shadow 0.6s ease-in-out;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.clicks-block .range span {
    white-space: nowrap;
    color: aqua;
    font-size: 1.1rem;
    z-index: 10;
}

/* .clicks-block .range.glow-on {
    box-shadow: 0 0 15px 5px hsl(186 100% 69%);
} */
.glow-on {
    box-shadow: 0 0 15px 5px hsl(186 100% 69%);
}

.range .full {
    position: absolute;
    top: 0;
    left: 0;
    max-width: 100%;
    background-color: yellow;
    height: 100%;
}

/* .range span {
    color: aqua;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.1rem;
    white-space: pre-line;
} */

.top-left .menu button:first-of-type {
    border-top: 3px groove #ffffffa6;
    border-bottom: 2px groove blue;
    background: radial-gradient(ellipse at bottom, navy 0, blue, white 100%);
}

.top-left .menu button:last-child {
    border-top: 2px groove blue;
    border-bottom: 3px groove ffffffa6;
    background: radial-gradient(ellipse at top, navy 0, blue, white 100%);
}

.top-left .menu .action-btn {
    margin: 0.625rem;
    width: 100%;
    background: radial-gradient(circle at 50%, navy 0, blue, white 120%);
    font-size: 1.1rem;
    color: white;
    border-radius: 20px;
    text-align: center;
    padding: 0.4rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
}

/* .top-left .menu>button img {
    position: relative;
    margin-top: -10px;
    margin-bottom: -10px;
    height: 25px;
} */

.top-left .menu>button img:last-child {
    margin-right: -10px;
    margin-left: 0;
}

.top-left button.reset-btn {
    background: radial-gradient(#b02c19, rgb(247, 79, 79), white);
}

.header-name {
    text-decoration: underline;
}

.header-name:hover {
    cursor: pointer;
}

.item div {
    width: 100px;
    margin-left: 5px;
    text-align: left;
}

@media screen and (max-width: 1200px) {
    .header-name {
        margin: 0.5rem 0;
    }

    /* .top-header {
        position: relative;
        left: 0;
    }

    .top-left {
        position: relative;
    } */
    .top-left {
        top: 10px;
        left: 10px;
        padding: 0;
        padding-top: 5px;
    }

    .top-left.margin-left {
        left: 60px;
    }

    .top-left .menu .action-btn {
        font-size: 0px;
        flex-direction: column;
        /* align-items: center; */
        padding: 5px 0;
    }

    .clicks-block {
        width: 4rem;
        left: 50%;
        /* transform: translateX(-50%); */
    }

    .clicks-block>p {
        font-size: 0px;
    }

    .clicks-block .range {
        width: 100%;
        height: 4rem;
        border-radius: 50%;
    }

    .music-btn-wrap {
        font-size: 0px;
    }

    .music-btn {
        margin: 0;
    }

    .top-left .menu>button {
        background: none !important;
        border: 2px solid rgba(255, 255, 255, 0.532) !important;
        width: 45px;
    }

    .top-left .menu>button img {
        margin: 0 !important;

    }
}

@media screen and (max-width: 900px) {
    /* .top-header {
        left: calc((100% - 250px) / 2);
        transform: translateX(-20%);
    } */

    .clicks-block {
        margin-top: 0;
    }

    .music-btn {
        box-shadow: 0 0 0px 2px white;
    }
}

@media screen and (max-height: 500px) and (min-width: 1201px) {
    .top-header {
        left: 250px;
        transform: translateX(20%);
    }
}

@media screen and (max-height: 500px) and (max-width: 1200px) {
    .top-header {
        left: 180px;
    }
}

@media screen and (max-height: 450px) {
    .top-header {
        left: 150px;
    }

    .top-left {
        height: 100dvh;
        min-height: fit-content;
        top: 0;
        overflow-x: visible;
        overflow-y: scroll;
    }

    .top-left .menu .action-btn {
        margin: unset;
    }

    .top-left .menu {
        height: calc(100% - 60px);
        padding: 0 8px 5px 5px;
        justify-content: space-evenly;
    }

    .top-left .menu>button {
        margin: auto;
        padding-top: 5px;
    }

    .clicks-block {
        width: 3rem;
        left: 0;
        transform: none;
    }

    .clicks-block .range {
        height: 3rem;
    }

    .clicks-block .range span {
        font-size: 0.9rem;
    }

    .action-btn .menu-btn-img {
        height: 25px;
    }

    .action-btn .menu-btn-img-mini {
        height: 15px;
    }

    .action-btn .btn-span {
        display: none;
    }

    h3 {
        font-size: 1.1rem;
    }
}

@media screen and (max-height: 300px) {
    .action-btn .menu-btn-img {
        height: 20px;
    }

    .action-btn .menu-btn-img-mini {
        height: 15px;
    }
}
</style>